/**
 * HMS (Hardware Management System) Error Code Parser
 * Based on BambuStudio's C++ implementation
 */

// Module IDs matching BambuStudio's ModuleID enum
export enum ModuleID {
    MODULE_UKNOWN = 0x00,
    MODULE_01 = 0x01,
    MODULE_02 = 0x02,
    MODULE_MC = 0x03,           // Main Controller
    MODULE_04 = 0x04,
    MODULE_MAINBOARD = 0x05,    // Main Board
    MODULE_06 = 0x06,
    MODULE_AMS = 0x07,          // Automatic Material System
    MODULE_TH = 0x08,           // Tool Head
    MODULE_09 = 0x09,
    MODULE_10 = 0x0A,
    MODULE_11 = 0x0B,
    MODULE_XCAM = 0x0C,         // X Camera
    MODULE_13 = 0x0D,
    MODULE_14 = 0x0E,
    MODULE_15 = 0x0F,
    MODULE_MAX = 0x10
}

// HMS Message Levels matching BambuStudio's HMSMessageLevel enum
export enum HMSMessageLevel {
    HMS_UNKNOWN = 0,
    HMS_FATAL = 1,
    HMS_SERIOUS = 2,
    HMS_COMMON = 3,
    HMS_INFO = 4,
    HMS_MSG_LEVEL_MAX = 5
}

// HMS Item interface matching the C++ HMSItem structure
export interface HMSItem {
    module_id: ModuleID;
    module_num: number;
    part_id: number;
    reserved: number;
    msg_level: HMSMessageLevel;
    msg_code: number;
    already_read?: boolean;
}

// HMS Error Message interface
export interface HMSErrorMessage {
    ecode: string;
    intro: string;
}

// HMS Action interface
export interface HMSAction {
    ecode: string;
    image: string;
    actions: number[];
    device: string;
}

// HMS JSON structure
export interface HMSJsonData {
    result: number;
    t: number;
    ver: number;
    data: {
        device_error: {
            ver: number;
            [langCode: string]: HMSErrorMessage[] | number;
        };
    };
}

// HMS Action JSON structure
export interface HMSActionJsonData {
    result: number;
    t: number;
    ver: number;
    data: HMSAction[];
}

// HMS Query class for managing HMS data
export class HMSQuery {
    private hmsInfoJsons: Map<string, HMSJsonData> = new Map();
    private hmsActionJsons: Map<string, HMSActionJsonData> = new Map();
    private hmsLocalImages: Map<string, string> = new Map(); // image name -> base64 or path
    private cloudHmsLastUpdateTime: Map<string, number> = new Map();

    /**
     * Load HMS error messages from JSON file
     * @param deviceIdType - Device ID type (e.g., "094")
     * @param langCode - Language code (e.g., "en", "zh-cn")
     * @returns Promise<boolean> - True if loaded successfully
     */
    async loadHmsInfo(deviceIdType: string, langCode: string = "en"): Promise<boolean> {
        try {
            const fileName = `hms_${langCode}_${deviceIdType}.json`;
            const response = await fetch(fileName);
            
            if (!response.ok) {
                console.warn(`Failed to load HMS info file: ${fileName}`);
                return false;
            }
            
            const data = await response.json() as unknown as HMSJsonData;
            this.hmsInfoJsons.set(deviceIdType, data);
            return true;
        } catch (error) {
            console.error(`Error loading HMS info for device ${deviceIdType}:`, error);
            return false;
        }
    }

    /**
     * Load HMS actions from JSON file
     * @param deviceIdType - Device ID type (e.g., "094")
     * @returns Promise<boolean> - True if loaded successfully
     */
    async loadHmsActions(deviceIdType: string): Promise<boolean> {
        try {
            const fileName = `hms_action_${deviceIdType}.json`;
            const response = await fetch(fileName);
            
            if (!response.ok) {
                console.warn(`Failed to load HMS actions file: ${fileName}`);
                return false;
            }
            
            const data = await response.json() as unknown as HMSActionJsonData;
            this.hmsActionJsons.set(deviceIdType, data);
            return true;
        } catch (error) {
            console.error(`Error loading HMS actions for device ${deviceIdType}:`, error);
            return false;
        }
    }

    /**
     * Query HMS error message for a given error code
     * @param deviceIdType - Device ID type
     * @param longErrorCode - Long error code (e.g., "07FFC010")
     * @param langCode - Language code (default: "en")
     * @returns Human-readable error message or empty string if not found
     */
    queryHmsMsg(deviceIdType: string, longErrorCode: string, langCode: string = "en"): string {
        if (longErrorCode.length === 0) {
            return "";
        }

        const hmsInfo = this.hmsInfoJsons.get(deviceIdType);
        if (!hmsInfo) {
            console.error(`No HMS info found for device: ${deviceIdType}`);
            return "";
        }

        const deviceError = hmsInfo.data?.device_error;
        if (!deviceError) {
            console.error("Invalid HMS info structure: missing device_error");
            return "";
        }

        // Try to get messages for the specified language
        const langMessages = deviceError[langCode];
        if (langMessages && Array.isArray(langMessages)) {
            for (const item of langMessages) {
                if (item.ecode && item.ecode.toUpperCase() === longErrorCode.toUpperCase() && item.intro) {
                    return item.intro;
                }
            }
        }

        // Fallback: search through all languages if specified language not found
        if (langCode !== "en") {
            for (const [lang, messages] of Object.entries(deviceError)) {
                if (lang === "ver") continue; // Skip version field
                
                if (Array.isArray(messages)) {
                    for (const item of messages) {
                        if (item.ecode && item.ecode.toUpperCase() === longErrorCode.toUpperCase() && item.intro) {
                            console.log(`Found message in language: ${lang}`);
                            return item.intro;
                        }
                    }
                }
            }
        }

        console.error(`No HMS message found for error code: ${longErrorCode}, device: ${deviceIdType}, lang: ${langCode}`);
        return "";
    }

    /**
     * Query HMS action for a given error code
     * @param deviceIdType - Device ID type
     * @param longErrorCode - Long error code
     * @returns HMS action object or null if not found
     */
    queryHmsAction(deviceIdType: string, longErrorCode: string): HMSAction | null {
        const hmsAction = this.hmsActionJsons.get(deviceIdType);
        if (!hmsAction) {
            return null;
        }

        for (const item of hmsAction.data) {
            if (item.ecode && item.ecode.toUpperCase() === longErrorCode.toUpperCase()) {
                if (item.device && (item.device.toUpperCase() === deviceIdType.toUpperCase() || item.device === "default")) {
                    return item;
                }
            }
        }

        return null;
    }

    /**
     * Check if an error is internal (has no user-facing message)
     * @param deviceIdType - Device ID type
     * @param longErrorCode - Long error code
     * @param langCode - Language code
     * @returns True if internal error
     */
    isInternalError(deviceIdType: string, longErrorCode: string, langCode: string = "en"): boolean {
        const message = this.queryHmsMsg(deviceIdType, longErrorCode, langCode);
        return message.length === 0;
    }

    /**
     * Get all available languages for a device
     * @param deviceIdType - Device ID type
     * @returns Array of language codes
     */
    getAvailableLanguages(deviceIdType: string): string[] {
        const hmsInfo = this.hmsInfoJsons.get(deviceIdType);
        if (!hmsInfo?.data?.device_error) {
            return [];
        }

        return Object.keys(hmsInfo.data.device_error).filter(key => key !== "ver");
    }

    /**
     * Clear all loaded HMS data
     */
    clearHmsInfo(): void {
        this.hmsInfoJsons.clear();
        this.hmsActionJsons.clear();
        this.hmsLocalImages.clear();
        this.cloudHmsLastUpdateTime.clear();
    }
}

// Global HMS Query instance
export const hmsQuery = new HMSQuery();

/**
 * Parse HMS info from attribute and code values
 * @param attr - Attribute value (32-bit unsigned integer)
 * @param code - Code value (32-bit unsigned integer)
 * @returns HMSItem object with parsed components
 */
export function parseHmsInfo(attr: number, code: number): HMSItem {
    // Extract components using bit shifting (matching C++ implementation)
    const module_id_int = (attr >> 24) & 0xFF;
    const module_num = (attr >> 16) & 0xFF;
    const part_id = (attr >> 8) & 0xFF;
    const reserved = (attr >> 0) & 0xFF;
    
    // Parse message level from code
    const msg_level_int = code >> 16;
    const msg_level = msg_level_int < HMSMessageLevel.HMS_MSG_LEVEL_MAX 
        ? msg_level_int as HMSMessageLevel 
        : HMSMessageLevel.HMS_UNKNOWN;
    
    // Extract message code
    const msg_code = code & 0xFFFF;
    
    return {
        module_id: module_id_int as ModuleID,
        module_num,
        part_id,
        reserved,
        msg_level,
        msg_code,
        already_read: false
    };
}

/**
 * Generate long error code string from HMS item
 * @param hmsItem - HMSItem object
 * @returns Formatted error code string (e.g., "07FFC010")
 */
export function getLongErrorCode(hmsItem: HMSItem): string {
    return `${hmsItem.module_id.toString(16).padStart(2, '0').toUpperCase()}` +
           `${hmsItem.module_num.toString(16).padStart(2, '0').toUpperCase()}` +
           `${hmsItem.part_id.toString(16).padStart(2, '0').toUpperCase()}` +
           `00000` +
           `${hmsItem.msg_level.toString(16).toUpperCase()}` +
           `${hmsItem.msg_code.toString(16).padStart(4, '0').toUpperCase()}`;
}

/**
 * Generate long error code directly from attribute and code values
 * @param attr - Attribute value
 * @param code - Code value
 * @returns Formatted error code string
 */
export function getLongErrorCodeFromRaw(attr: number, code: number): string {
    const hmsItem = parseHmsInfo(attr, code);
    return getLongErrorCode(hmsItem);
}

/**
 * Get human-readable module name
 * @param moduleId - Module ID
 * @returns Module name string
 */
export function getModuleName(moduleId: ModuleID): string {
    switch (moduleId) {
        case ModuleID.MODULE_MC:
            return "MC";
        case ModuleID.MODULE_MAINBOARD:
            return "MainBoard";
        case ModuleID.MODULE_AMS:
            return "AMS";
        case ModuleID.MODULE_TH:
            return "TH";
        case ModuleID.MODULE_XCAM:
            return "XCam";
        default:
            return `Unknown0x${moduleId.toString(16).toUpperCase()}`;
    }
}

/**
 * Get human-readable message level string
 * @param level - HMS message level
 * @returns Message level string
 */
export function getHmsMessageLevelString(level: HMSMessageLevel): string {
    switch (level) {
        case HMSMessageLevel.HMS_FATAL:
            return "Fatal";
        case HMSMessageLevel.HMS_SERIOUS:
            return "Serious";
        case HMSMessageLevel.HMS_COMMON:
            return "Common";
        case HMSMessageLevel.HMS_INFO:
            return "Info";
        default:
            return "Unknown";
    }
}

/**
 * Parse HMS info and return a detailed object with all information
 * @param attr - Attribute value
 * @param code - Code value
 * @returns Detailed HMS info object
 */
export function parseHmsInfoDetailed(attr: number, code: number) {
    const hmsItem = parseHmsInfo(attr, code);
    const longErrorCode = getLongErrorCode(hmsItem);
    
    return {
        ...hmsItem,
        longErrorCode,
        moduleName: getModuleName(hmsItem.module_id),
        messageLevelString: getHmsMessageLevelString(hmsItem.msg_level),
        // Additional computed properties
        isFatal: hmsItem.msg_level === HMSMessageLevel.HMS_FATAL,
        isSerious: hmsItem.msg_level === HMSMessageLevel.HMS_SERIOUS,
        isCommon: hmsItem.msg_level === HMSMessageLevel.HMS_COMMON,
        isInfo: hmsItem.msg_level === HMSMessageLevel.HMS_INFO,
        isUnknown: hmsItem.msg_level === HMSMessageLevel.HMS_UNKNOWN
    };
}

/**
 * Validate if a long error code string is properly formatted
 * @param errorCode - Error code string to validate
 * @returns True if valid, false otherwise
 */
export function isValidLongErrorCode(errorCode: string): boolean {
    // Should be exactly 16 characters: 2+2+2+5+1+4 = 16
    if (errorCode.length !== 16) {
        return false;
    }
    
    // Should contain only hexadecimal characters
    const hexRegex = /^[0-9A-Fa-f]+$/;
    if (!hexRegex.test(errorCode)) {
        return false;
    }
    
    // Check that the middle section is "00000"
    if (errorCode.substring(6, 11) !== "00000") {
        return false;
    }
    
    return true;
}

/**
 * Parse a long error code string back into an HMS item
 * @param longErrorCode - Error code string (e.g., "07FFC010")
 * @returns HMSItem object or null if invalid
 */
export function parseLongErrorCode(longErrorCode: string): HMSItem | null {
    if (!isValidLongErrorCode(longErrorCode)) {
        return null;
    }
    
    try {
        const moduleId = parseInt(longErrorCode.substring(0, 2), 16) as ModuleID;
        const moduleNum = parseInt(longErrorCode.substring(2, 4), 16);
        const partId = parseInt(longErrorCode.substring(4, 6), 16);
        const msgLevel = parseInt(longErrorCode.substring(11, 12), 16) as HMSMessageLevel;
        const msgCode = parseInt(longErrorCode.substring(12, 16), 16);
        
        return {
            module_id: moduleId,
            module_num: moduleNum,
            part_id: partId,
            reserved: 0, // Not stored in long error code
            msg_level: msgLevel,
            msg_code: msgCode,
            already_read: false
        };
    } catch {
        return null;
    }
}

// Utility functions for common HMS operations

/**
 * Check if an HMS item represents a fatal error
 * @param hmsItem - HMS item to check
 * @returns True if fatal error
 */
export function isFatalError(hmsItem: HMSItem): boolean {
    return hmsItem.msg_level === HMSMessageLevel.HMS_FATAL;
}

/**
 * Check if an HMS item represents a serious error
 * @param hmsItem - HMS item to check
 * @returns True if serious error
 */
export function isSeriousError(hmsItem: HMSItem): boolean {
    return hmsItem.msg_level === HMSMessageLevel.HMS_SERIOUS;
}

/**
 * Check if an HMS item represents a common error
 * @param hmsItem - HMS item to check
 * @returns True if common error
 */
export function isCommonError(hmsItem: HMSItem): boolean {
    return hmsItem.msg_level === HMSMessageLevel.HMS_COMMON;
}

/**
 * Check if an HMS item represents an informational message
 * @param hmsItem - HMS item to check
 * @returns True if informational
 */
export function isInfoMessage(hmsItem: HMSItem): boolean {
    return hmsItem.msg_level === HMSMessageLevel.HMS_INFO;
}

/**
 * Get error severity as a number (higher = more severe)
 * @param hmsItem - HMS item to check
 * @returns Severity number (0-4)
 */
export function getErrorSeverity(hmsItem: HMSItem): number {
    return hmsItem.msg_level;
}

/**
 * Check if an HMS item is from a specific module
 * @param hmsItem - HMS item to check
 * @param moduleId - Module ID to check against
 * @returns True if from specified module
 */
export function isFromModule(hmsItem: HMSItem, moduleId: ModuleID): boolean {
    return hmsItem.module_id === moduleId;
}

/**
 * Check if an HMS item is from the AMS module
 * @param hmsItem - HMS item to check
 * @returns True if from AMS module
 */
export function isAmsError(hmsItem: HMSItem): boolean {
    return isFromModule(hmsItem, ModuleID.MODULE_AMS);
}

/**
 * Check if an HMS item is from the main controller
 * @param hmsItem - HMS item to check
 * @returns True if from main controller
 */
export function isMcError(hmsItem: HMSItem): boolean {
    return isFromModule(hmsItem, ModuleID.MODULE_MC);
}

/**
 * Check if an HMS item is from the tool head
 * @param hmsItem - HMS item to check
 * @returns True if from tool head
 */
export function isThError(hmsItem: HMSItem): boolean {
    return isFromModule(hmsItem, ModuleID.MODULE_TH);
}

/**
 * Check if an HMS item is from the X camera
 * @param hmsItem - HMS item to check
 * @returns True if from X camera
 */
export function isXcamError(hmsItem: HMSItem): boolean {
    return isFromModule(hmsItem, ModuleID.MODULE_XCAM);
}

// High-level convenience functions for complete HMS error handling

/**
 * Complete HMS error parsing with human-readable message
 * @param deviceIdType - Device ID type (e.g., "094")
 * @param attr - Attribute value
 * @param code - Code value
 * @param langCode - Language code (default: "en")
 * @returns Complete HMS error information
 */
export async function parseHmsErrorComplete(
    deviceIdType: string,
    attr: number,
    code: number,
    langCode: string = "en"
): Promise<{
    hmsItem: HMSItem;
    longErrorCode: string;
    errorMessage: string;
    action: HMSAction | null;
    isInternal: boolean;
    moduleName: string;
    messageLevelString: string;
}> {
    // Parse HMS info
    const hmsItem = parseHmsInfo(attr, code);
    const longErrorCode = getLongErrorCode(hmsItem);
    
    // Load HMS data if not already loaded
    await hmsQuery.loadHmsInfo(deviceIdType, langCode);
    await hmsQuery.loadHmsActions(deviceIdType);
    
    // Get error message and action
    const errorMessage = hmsQuery.queryHmsMsg(deviceIdType, longErrorCode, langCode);
    const action = hmsQuery.queryHmsAction(deviceIdType, longErrorCode);
    const isInternal = hmsQuery.isInternalError(deviceIdType, longErrorCode, langCode);
    
    return {
        hmsItem,
        longErrorCode,
        errorMessage,
        action,
        isInternal,
        moduleName: getModuleName(hmsItem.module_id),
        messageLevelString: getHmsMessageLevelString(hmsItem.msg_level)
    };
}

/**
 * Get HMS error message for a long error code
 * @param deviceIdType - Device ID type
 * @param longErrorCode - Long error code
 * @param langCode - Language code (default: "en")
 * @returns Human-readable error message
 */
export async function getHmsErrorMessage(
    deviceIdType: string,
    longErrorCode: string,
    langCode: string = "en"
): Promise<string> {
    await hmsQuery.loadHmsInfo(deviceIdType, langCode);
    return hmsQuery.queryHmsMsg(deviceIdType, longErrorCode, langCode);
}

/**
 * Get HMS action for a long error code
 * @param deviceIdType - Device ID type
 * @param longErrorCode - Long error code
 * @returns HMS action object or null
 */
export async function getHmsAction(
    deviceIdType: string,
    longErrorCode: string
): Promise<HMSAction | null> {
    await hmsQuery.loadHmsActions(deviceIdType);
    return hmsQuery.queryHmsAction(deviceIdType, longErrorCode);
} 